import { useNavigate } from 'react-router-dom';
import { Eye, FilePdf, PaperPlaneTilt, Trash, PencilSimple, CaretUp, CaretDown, CaretUpDown, SealCheck, DotsThreeVertical } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canVerify } from '@/auth/AuthContext';
import { STAGE_BADGE, computeStage, isApplicant } from '@/lib/workflow';
import { getFormStartDate, formatDate, formatDateOnly, formTypeLabel } from './historyUtils';

export function HistoryTable({
  submissions, selectedIds, allSelected,
  onToggleSelect, onToggleSelectAll,
  sortKey, sortDir, onToggleSort,
  wfEnabled, stagesByType, canPrintCheck,
  onPrintCheck, onDelete, onResend,
}) {
  const navigate = useNavigate();
  const { t } = useT();
  const { user } = useAuth();

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <CaretUpDown size={12} weight="bold" className="text-zinc-300 group-hover:text-zinc-400 transition-colors" />;
    return sortDir === 'asc'
      ? <CaretUp size={12} weight="bold" className="text-teal-700" />
      : <CaretDown size={12} weight="bold" className="text-teal-700" />;
  };

  const renderStageBadge = (submission) => {
    if (!wfEnabled) return null;
    const st = computeStage(submission, stagesByType[submission.form_type]);
    const b = STAGE_BADGE[st] || STAGE_BADGE.awaiting_sign;
    const stageLabel = t(`hist.status.${STAGE_BADGE[st] ? st : 'awaiting_sign'}`);
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-[10px] font-semibold rounded-full border px-1.5 py-0.5 shrink-0 ${b.cls}`}
        title={t('hist.stageTip', { label: stageLabel })}
        data-testid={`stage-badge-${submission.id}`}
      >
        <SealCheck size={11} weight="fill" /> {stageLabel}
      </span>
    );
  };

  const renderPrintCheck = (submission) => {
    const pc = submission.print_check;
    const allowed = canPrintCheck(submission);
    const title = pc?.checked
      ? t('hist.pc.checkedBy', { name: pc.by_name || '—', date: formatDate(pc.at) })
      : allowed ? t('hist.pc.markTip') : t('hist.pc.approverOnly');
    return (
      <div className="inline-flex flex-col items-center gap-0.5" title={title}>
        <Checkbox
          checked={!!pc?.checked}
          disabled={!allowed}
          onCheckedChange={(v) => onPrintCheck(submission, !!v)}
          data-testid={`print-check-${submission.id}`}
          className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
        />
        {pc?.checked && (
          <span className="text-[10px] leading-tight text-zinc-500 truncate max-w-[72px]" data-testid={`print-check-by-${submission.id}`}>
            {pc.by_name}
          </span>
        )}
      </div>
    );
  };

  const renderActions = (submission) => (
    <div className="flex items-center justify-end gap-0.5 sm:gap-1">
      <button
        onClick={() => navigate(`/submission/${submission.id}`)}
        data-testid={`action-view-btn-${submission.id}`}
        className="p-1.5 sm:p-2 text-zinc-600 hover:text-teal-700 hover:bg-blue-50 rounded-md transition-colors"
        title={t('hist.action.view')}
      >
        <Eye size={18} weight="regular" />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`action-menu-btn-${submission.id}`}
            className="p-1.5 sm:p-2 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-md transition-colors data-[state=open]:bg-zinc-100 data-[state=open]:text-zinc-900"
            title={t('hist.action.more')}
          >
            <DotsThreeVertical size={18} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem
            onClick={() => navigate(`/print?ids=${submission.id}&mode=pdf`)}
            data-testid={`action-pdf-btn-${submission.id}`}
            className="cursor-pointer"
          >
            <FilePdf size={16} weight="regular" className="mr-2 text-rose-600" /> {t('hist.action.pdf')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate(`/submission/${submission.id}/edit`)}
            data-testid={`action-edit-btn-${submission.id}`}
            className="cursor-pointer"
          >
            <PencilSimple size={16} weight="regular" className="mr-2 text-amber-600" /> {t('hist.action.edit')}
          </DropdownMenuItem>
          {wfEnabled && (canVerify(user) || isApplicant(user, submission)) && (
            <DropdownMenuItem
              onClick={() => navigate(`/verify/${submission.id}`)}
              data-testid={`action-verify-btn-${submission.id}`}
              className="cursor-pointer"
            >
              <SealCheck size={16} weight="regular" className="mr-2 text-emerald-600" /> {t('hist.action.verify')}
            </DropdownMenuItem>
          )}
          {submission.form_type === 'leave' && (
            <DropdownMenuItem
              onClick={() => onResend(submission.id)}
              data-testid={`action-resend-btn-${submission.id}`}
              className="cursor-pointer"
            >
              <PaperPlaneTilt size={16} weight="regular" className="mr-2 text-sky-600" /> {t('hist.action.resend')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onDelete(submission.id)}
            data-testid={`action-delete-btn-${submission.id}`}
            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <Trash size={16} weight="regular" className="mr-2" /> {t('hist.action.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-zinc-100 border-b-2 border-zinc-300">
            <tr>
              <th className="px-2 sm:px-4 py-3 w-8 sm:w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onToggleSelectAll}
                  data-testid="select-all-checkbox"
                />
              </th>
              <th
                onClick={() => onToggleSort('date')}
                data-testid="sort-date"
                className="group px-2 sm:px-4 lg:px-6 py-3 text-left text-[11px] sm:text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{t('hist.th.date')} <SortIcon k="date" /></span>
              </th>
              <th
                onClick={() => onToggleSort('type')}
                data-testid="sort-type"
                className="group hidden sm:table-cell px-2 sm:px-4 lg:px-6 py-3 text-left text-[11px] sm:text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{t('hist.th.type')} <SortIcon k="type" /></span>
              </th>
              <th
                onClick={() => onToggleSort('name')}
                data-testid="sort-name"
                className="group px-2 sm:px-4 lg:px-6 py-3 text-left text-[11px] sm:text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{t('hist.th.name')} <SortIcon k="name" /></span>
              </th>
              <th
                onClick={() => onToggleSort('department')}
                data-testid="sort-department"
                className="group hidden md:table-cell px-4 lg:px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{t('hist.th.dept')} <SortIcon k="department" /></span>
              </th>
              <th
                onClick={() => onToggleSort('printed')}
                data-testid="sort-printed"
                className="group hidden lg:table-cell px-4 lg:px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center gap-1">{t('hist.th.printed')} <SortIcon k="printed" /></span>
              </th>
              <th
                onClick={() => onToggleSort('print_check')}
                data-testid="sort-print-check"
                className="group px-1 sm:px-3 py-3 text-center text-[11px] sm:text-xs font-semibold text-zinc-700 uppercase tracking-wider cursor-pointer select-none hover:bg-zinc-100 transition-colors whitespace-nowrap"
              >
                <span className="inline-flex items-center justify-center gap-1">{t('hist.th.printCheck')} <SortIcon k="print_check" /></span>
              </th>
              <th className="px-2 sm:px-4 lg:px-6 py-3 text-right text-[11px] sm:text-xs font-semibold text-zinc-700 uppercase tracking-wider whitespace-nowrap">
                {t('hist.th.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {submissions.map((submission) => (
              <tr
                key={submission.id}
                className={`hover:bg-zinc-50 transition-colors ${
                  selectedIds.includes(submission.id) ? 'bg-blue-50' : ''
                }`}
                data-testid={`submission-row-${submission.id}`}
              >
                <td className="px-2 sm:px-4 py-2.5 sm:py-3">
                  <Checkbox
                    checked={selectedIds.includes(submission.id)}
                    onCheckedChange={() => onToggleSelect(submission.id)}
                    data-testid={`select-${submission.id}`}
                  />
                </td>
                <td className="px-2 sm:px-4 lg:px-6 py-2.5 sm:py-3 text-xs sm:text-sm text-zinc-900 tabular-nums whitespace-nowrap" title={t('hist.submittedTip', { date: formatDate(submission.created_at) })}>
                  <span className="sm:hidden">{formatDateOnly(getFormStartDate(submission)).replace(/^\d{4}\//, '')}</span>
                  <span className="hidden sm:inline">{formatDateOnly(getFormStartDate(submission))}</span>
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-4 lg:px-6 py-2.5 sm:py-3">
                  <span className="inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-700 rounded whitespace-nowrap">
                    {formTypeLabel(t, submission.form_type)}
                  </span>
                </td>
                <td className="px-2 sm:px-4 lg:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-medium text-zinc-900">
                  <span className="sm:hidden inline-block mb-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded whitespace-nowrap">
                    {formTypeLabel(t, submission.form_type)}
                  </span>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <EmployeeLink names={submission.employee_name} />
                    {renderStageBadge(submission)}
                  </div>
                </td>
                <td className="hidden md:table-cell px-4 lg:px-6 py-3 text-sm text-zinc-600 whitespace-nowrap">{submission.department || '-'}</td>
                <td className="hidden lg:table-cell px-4 lg:px-6 py-3 text-sm text-zinc-600 whitespace-nowrap" data-testid={`last-printed-${submission.id}`}>
                  {submission.last_printed_at
                    ? formatDate(submission.last_printed_at)
                    : <span className="text-zinc-400 italic">{t('hist.notPrinted')}</span>}
                </td>
                <td className="px-1 sm:px-3 py-2.5 sm:py-3 text-center" data-testid={`print-check-cell-${submission.id}`}>
                  {renderPrintCheck(submission)}
                </td>
                <td className="px-1 sm:px-4 lg:px-6 py-2.5 sm:py-3 text-right">
                  {renderActions(submission)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmployeeLink({ names }) {
  const navigate = useNavigate();
  const { t, lang } = useT();
  if (!names) return null;
  const list = String(names).split('、').map((s) => s.trim()).filter(Boolean);
  return (
    <span>
      {list.map((n, i) => (
        <span key={`${n}-${i}`}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate('/settings/employees'); }}
            className="text-teal-700 hover:underline focus:outline-none"
            title={t('hist.employeeEditTip', { name: n })}
            data-testid={`employee-link-${n}`}
          >
            {n}
          </button>
          {i < list.length - 1 ? (lang === 'en' ? ', ' : '、') : ''}
        </span>
      ))}
    </span>
  );
}
